import type { WorkflowDefinition } from "../types";

/**
 * W27 — Campaign visibility tracking.
 *
 * `perTarget` on a tag, like the crisis playbook: a campaign is not the
 * property, and two campaigns can be in flight at once. The tag is asked for
 * at start (default `campaign`), keys the run, and feeds the `promptTag`
 * predicate through `run.context.tag`, so starting again with the same tag
 * resumes the existing run rather than forking a parallel one.
 *
 * `since` predicates stay property-wide: a tag has no event slice of its own,
 * and annotating a flight date is a property-timeline act.
 */
export const campaignWorkflow: WorkflowDefinition = {
	id: "campaign",
	wNumber: 27,
	title: "Track campaign visibility in AI answers",
	outcome:
		"Check whether the campaign's messages reached AI answers, not just whether the coverage appeared.",
	audiences: ["pr", "agencies", "inhouse"],
	uses: [
		"Key Messages",
		"Prompts",
		"Projects",
		"Annotations",
		"Messages",
		"Coverage Groups",
	],
	scope: "property",
	concurrency: "perTarget",
	progress: "explicit",
	target: { kind: "tag", label: "Campaign tag", defaultTag: "campaign" },
	isApplicable: (mode) => mode.showProjects && mode.showKeyMessages,
	articleSlug: "how-to-track-campaign-visibility-in-ai-answers",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "messages",
			title: "Write down the messages the campaign must land",
			page: "Key Messages",
			route: "/key-messages",
			criterion: "You've added a positive key message since you started.",
			why: 'Only claims written in measurable language can be reported on later. "Good for the environment" cannot be scored.',
			docsHref: "/docs/dashboards/key-messages",
			tasks: [
				"Write three to six claims as sentences, before launch",
				"Add the claim you are pushing against as a negative message",
				"Tag them with the campaign",
			],
			// A campaign installs *its own* claims. The always-on brand messages
			// were already there and are not what this run is measuring.
			complete: { kind: "since", event: "positiveKeyMessageAdded" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Campaign claims tracked",
							value: state.property.keyMessages.positive,
							target: 1,
						}
					: null,
		},
		{
			id: "prompts",
			title: "Turn the campaign into prompts you can track",
			page: "Prompts",
			route: "/discovery-queries",
			criterion: "You've tagged prompts for this campaign.",
			why: "Your standing prompts ask about the brand, so a real win can vanish into the average. Most campaign prompts should name no brand.",
			docsHref: "/docs/dashboards/discovery-queries",
			tasks: [
				"Add four to eight problem questions that name no brand",
				"Tag every one of them with the campaign tag",
				"Start tracking two weeks before launch",
			],
			complete: { kind: "promptTag", tag: "campaign" },
			signal: ({ state, run }) => {
				if (!state.property) {
					return null;
				}
				const tag = run?.context.tag ?? "campaign";
				return {
					kind: "checklist",
					items: [
						{
							label: `Prompts tagged "${tag}"`,
							done: state.property.promptTags.includes(tag),
						},
					],
				};
			},
		},
		{
			id: "project",
			title: "Set a target outlet list you can score",
			page: "Projects",
			route: "/projects/new",
			criterion:
				"You've created a project with at least one publisher goal.",
			why: "Add one more filter to the media list. Can AI read the outlet, and is it already cited for your topic?",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Score the target outlets before committing outreach hours",
				"Attach the list to the campaign's project as goals",
				"Set the project dates to the campaign flight",
			],
			// Goal rows carry no timestamp of their own, so the project is what
			// anchors this: a project created for the campaign, with the target
			// outlet list attached to it.
			complete: {
				kind: "all",
				of: [
					{ kind: "since", event: "projectCreated" },
					{ kind: "count", key: "goalTypes.PUBLISHER", gte: 1 },
				],
			},
			spend: { kind: "trackedPrompts", count: "fromState" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Publisher goals",
							value: state.property.goalTypes.PUBLISHER,
							target: 1,
						}
					: null,
		},
		{
			id: "annotate",
			title: "Mark the flight dates and the placements",
			page: "Annotations",
			route: "/annotations",
			criterion: "You've noted a campaign date since you started.",
			why: "Five minutes here saves the retrospective. Six weeks later a line jumps and you can say what caused it.",
			docsHref: "/docs/dashboards/annotations",
			tasks: [
				"Mark the launch, each wave and every major placement",
				"Mark outside events that could move the numbers",
				"Mark the placement date, not the day you found the clip",
			],
			complete: { kind: "since", event: "annotationCreated" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Dates on the timeline",
							value: state.property.manualAnnotationCount,
						}
					: null,
		},
		{
			id: "monitor",
			title: "Watch the messages land while it is live",
			page: "Messages",
			route: "/dashboard/messages",
			criterion: "You've got daily data for this brand.",
			why: "Message pull-through asks whether AI repeats your claim when no brand is named. Share of voice asks whether you show up at all.",
			docsHref: "/docs/dashboards/brand-dashboard",
			tasks: [
				"Filter to the campaign tag and read weekly, not daily",
				"Expect three to six weeks before pull-through moves",
				"If one message stays flat, look at its wording",
			],
			complete: { kind: "flag", key: "hasNightlyMetrics" },
			enrichment: "messageTrend",
			// The series comes from the message-tracking hook in the step panel.
			signal: () => null,
		},
		{
			id: "coverage",
			title: "Group the coverage and see what AI cites",
			page: "Coverage Groups",
			route: "/coverage-groups",
			criterion: "You've created a coverage group since you started.",
			why: "One group lets you measure the coverage as a set. The split it gives you beats reach numbers for targeting next time.",
			docsHref: "/docs/dashboards/coverage-groups",
			tasks: [
				"Paste the campaign's placement URLs into one group as they land",
				"Read its AI Citations page for what was actually used",
				"Check the wider citation mix for your next pitch list",
			],
			// The campaign's placements go in a group built for the campaign.
			complete: { kind: "since", event: "coverageGroupCreated" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Coverage groups",
							value: state.property.coverageGroupCount,
							target: 1,
						}
					: null,
		},
		{
			id: "wrap",
			title: "Close the campaign and export the retrospective",
			page: "Projects",
			route: "/projects",
			criterion: "You've completed the project since you started.",
			why: "Export while it is fresh, with your notes already on the chart. That beats a summary written from memory.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Complete the project and export the retrospective",
				"Leave the campaign prompts tracking for a few weeks",
				"Set the next campaign's baseline before it launches",
			],
			optional: true,
			complete: { kind: "since", event: "projectCompleted" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Completed projects",
							value: state.property.completedProjectCount,
						}
					: null,
		},
	],
};
